/**
 * Der Anker eines Befundes, die Markierung, aus der er entsteht — und die
 * Zeilen, die er überhaupt nennen darf.
 *
 * Ein Befund zeigt auf **Projekt, Pfad, Revision und Zeilenbereich** — nie auf
 * eine Position innerhalb eines Diffs (ADR-0006). Für die fortlaufende
 * Begutachtung ist der Unterschied ununterscheidbar; für den Migrationsmodus und
 * für jeden Eigenbefund an Code, den die Revision gar nicht angefasst hat, ist er
 * die Voraussetzung.
 *
 * Die Revision im Anker ist dabei **immer die begutachtete** (ADR-0019). Der
 * Zweig auf die Vorgängerrevision ist weg, im Lesen wie im Schreiben: Sie ist
 * nicht, wo eine Zeile entstand, sondern nur die, in der die Datei zuletzt davor
 * geändert wurde — ein Anker dort benennt keinen Verursacher. Daraus folgt, was
 * diese Datei rechnet: **Es gibt keinen Befund an einer entfernten Zeile und
 * keinen an einer gelöschten Datei.** War das Entfernen selbst der Fehler, liegt
 * er auf der Zeile unmittelbar davor; im Diff steht die Löschung darunter, und
 * der Leser sieht sie.
 *
 * Deshalb steht das hier und nicht in der Ansicht: Es ist Fachlichkeit, keine
 * Darstellung, und es wird für sich geprüft. Dieselbe Frage beantwortet
 * serverseitig `FindableLines` — die Ansicht darf nicht anbieten, was die Eingabe
 * abweist.
 */

import type { ChangeAction, DiffHunk, DiffLineKind, Finding, SiteAnchor } from "../../api/client";
import { formatNumber } from "../../formatting";

/**
 * Die Seite, auf der eine Zeile liegt: `old` ist der Stand der
 * Vorgängerrevision, `new` der der begutachteten.
 *
 * Beide Seiten werden gezeigt, aber nur die neue trägt einen Befund: Was auf der
 * alten steht und auf der neuen fehlt, hat diese Revision entfernt, und damit ist
 * der Fehler weg (ADR-0019).
 */
export type LineSide = "old" | "new";

/**
 * Was von einer Dateiänderung nötig ist, um einen Anker zu setzen: Projekt, Pfad
 * und die begutachtete Revision.
 *
 * Bewusst nicht `FileChangeDetail`: Der Anker hängt nicht am Diff und nicht am
 * Dateiinhalt (ADR-0006), und die Dateiliste kennt beides gar nicht — die
 * Befundleiste soll trotzdem in jede Datei der Revision springen können, auch in
 * eine noch nicht geladene.
 */
export interface AnchoredFile {
  projectId: number;
  path: string;
  revisionNumber: number;
}

/**
 * Eine Markierung im Code: eine Zeile oder ein zusammenhängender Bereich, immer
 * innerhalb **einer** Seite.
 *
 * `origin` ist die zuerst angeklickte Zeile. Von ihr aus erweitert ein zweiter
 * Klick mit Umschalt den Bereich — nach oben wie nach unten, ohne dass die
 * Reihenfolge der Klicks etwas anderes ergäbe.
 */
export interface Marking {
  side: LineSide;
  origin: number;
  startLine: number;
  endLine: number;
}

/** Der Anker, den eine Markierung ergibt — genau das, was ein Befund trägt. */
export interface FindingAnchor {
  projectId: number;
  path: string;
  revision: number;
  startLine: number;
  endLine: number;
}

/** Ein zusammenhängender Bereich von Zeilen der neuen Seite. */
export interface LineRange {
  startLine: number;
  endLine: number;
}

/**
 * Was von einer Dateiänderung nötig ist, um die **erlaubten Zeilen** zu rechnen:
 * die Art der Änderung, der Diff und der abgelegte Inhalt.
 *
 * Anders als `AnchoredFile` hängt das am Diff, und deshalb steht es getrennt
 * davon: Wohin ein Sprung führt, weiß die Dateiliste; ob dort markiert werden
 * darf, weiß erst die geladene Dateiänderung.
 */
export interface FindableFile {
  changeAction: ChangeAction;
  hunks: DiffHunk[];
  content: string | null;
}

/**
 * Die Zeilen, die ein Befund dieser Dateiänderung nennen darf (ADR-0019).
 *
 * `withoutPlace` ist der Fall ohne jeden Ort — eine gelöschte oder eine
 * übersprungene Dateiänderung. Er steht getrennt von den Bereichen, weil er einen
 * eigenen Grund hat: „hier gar nicht" ist eine andere Auskunft als „diese Zeile
 * nicht".
 */
export interface FindableLines {
  ranges: LineRange[];
  withoutPlace: string | null;
}

/**
 * Auf welcher Seite eine Diff-Zeile liegt.
 *
 * Eine unveränderte Zeile steht in beiden Fassungen; sie zählt zur neuen, weil
 * ein Befund an ihr die begutachtete Revision meint — das ist der Stand, über
 * den geurteilt wird.
 */
export function sideOfDiffLine(kind: DiffLineKind): LineSide {
  return kind === "removed" ? "old" : "new";
}

/**
 * Auf welcher Seite die **vollständige Datei** liegt: immer auf der neuen.
 *
 * Auch bei einer gelöschten Datei, deren abgelegter Inhalt aus der
 * Vorgängerrevision stammt — der Anker nennt trotzdem die begutachtete Revision
 * (ADR-0019), und damit gibt es für die Markierung nur noch eine Seite. Der
 * Sprung an einen Anker und die Zeilen der vollständigen Datei rechnen hier
 * gemeinsam, damit die beiden nicht auseinanderlaufen können.
 */
export function sideOfWholeFile(): LineSide {
  return "new";
}

/**
 * Die Zeilen eines abgelegten Dateiinhalts, von 1 an gezählt am Index 0 — die
 * Entsprechung zu `ContentLines` im Server.
 *
 * Die Regel, die man dabei leicht übersieht, steht deshalb hier und nur hier:
 * Eine Datei endet mit einem Zeilenumbruch, und die leere Zeile dahinter ist
 * keine. Die vollständige Datei zeigt danach ihre Zeilen, und die erlaubten
 * Zeilen werden danach gekappt; zerfielen die beiden verschieden, ließe sich eine
 * Zeile markieren, die es nicht gibt.
 */
export function contentLines(content: string): string[] {
  const lines = content.split(/\r\n|\r|\n/);

  if (lines.length > 1 && lines[lines.length - 1] === "") {
    lines.pop();
  }

  return lines;
}

/** Eine einzelne Zeile markieren. */
export function markLine(side: LineSide, line: number): Marking {
  return { side, origin: line, startLine: line, endLine: line };
}

/**
 * Die Markierung bis zu einer weiteren Zeile ausdehnen.
 *
 * `null` heißt: **abgelehnt**. Eine Markierung liegt immer innerhalb einer Seite
 * — eine Auswahl über entfernte *und* erhaltene Zeilen nennte zwei Stände, und
 * ein Befund liegt in genau einem (ADR-0019).
 */
export function extendMarking(marking: Marking, side: LineSide, line: number): Marking | null {
  if (side !== marking.side) {
    return null;
  }

  return {
    side,
    origin: marking.origin,
    startLine: Math.min(marking.origin, line),
    endLine: Math.max(marking.origin, line),
  };
}

/** Liegt diese Zeile in der Markierung? Zeilen der anderen Seite nie. */
export function covers(marking: Marking | null, side: LineSide, line: number): boolean {
  if (marking === null || marking.side !== side) {
    return false;
  }

  return line >= marking.startLine && line <= marking.endLine;
}

/**
 * Der Anker zu einer Markierung — er nennt **immer die begutachtete Revision**
 * (ADR-0019).
 */
export function anchorOf(change: AnchoredFile, marking: Marking): FindingAnchor {
  return {
    projectId: change.projectId,
    path: change.path,
    revision: change.revisionNumber,
    startLine: marking.startLine,
    endLine: marking.endLine,
  };
}

/**
 * Der Anker einer **Stelle**, wie die Schnittstelle ihn nennt (`SiteAnchor`) —
 * die einzige Kennung, über die eine Stelle anzusprechen ist: Sie wird bei der
 * Abfrage gerechnet und hat keine eigene (ADR-0015).
 *
 * Genommen wird er von einem beliebigen Befund der Stelle, denn alle tragen
 * denselben: Genau daran sind sie zu einer Stelle zusammengefasst worden.
 * Dieselben fünf Angaben wie `FindingAnchor`, nur unter dem Namen, unter dem der
 * Server sie liest — zwei Schreibweisen für denselben Anker sind eine Kante des
 * Vertrags und keine Fachlichkeit.
 */
export function siteAnchorOf(found: Finding): SiteAnchor {
  const { projectId, path, revisionNumber, startLine, endLine } = found;

  return { projectId, path, revisionNumber, startLine, endLine };
}

/**
 * Der Schlüssel einer Stelle: **der ganze Anker**, die Revision eingeschlossen
 * (ADR-0006, ADR-0015).
 *
 * Ohne die Revision fielen zwei Befunde an denselben Zeilen zweier Stände in
 * einen Block. Genau so stand derselbe Text zweimal untereinander — einmal aus
 * r86.350, einmal aus r86.351 —, und es sah aus, als hätte das System
 * gestottert.
 *
 * Dieselbe Regel wie im Befundvorrat: Zwei Ansichten, die dieselbe Frage
 * verschieden beantworten, sind schlimmer als eine, die sie falsch beantwortet.
 *
 * Gefragt wird nach den vier Angaben und nicht nach einem bestimmten Typ: Denselben
 * Schlüssel bilden ein Befund, ein Anker und ein **Ausschnitt** — und genau daran
 * finden sie zueinander.
 */
export function siteKeyOf(
  anchor: FindingAnchor | Pick<Finding, "path" | "revisionNumber" | "startLine" | "endLine">,
): string {
  const revision = "revision" in anchor ? anchor.revision : anchor.revisionNumber;

  return [anchor.path, revision, anchor.startLine, anchor.endLine].join("@");
}

/** Der Anker eines abgelegten Befundes — er trägt ihn bereits vollständig. */
export function anchorOfFinding(found: Finding): FindingAnchor {
  return {
    projectId: found.projectId,
    path: found.path,
    revision: found.revisionNumber,
    startLine: found.startLine,
    endLine: found.endLine,
  };
}

/**
 * Die Markierung, an die ein abgelegter Befund zurückführt — der Sprung der
 * Befundleiste.
 */
export function markingOfFinding(found: Finding): Marking {
  return markingAt(anchorOfFinding(found));
}

/**
 * Die Markierung zu einem Anker — der Sprung aus dem **Befundvorrat**.
 *
 * Er nimmt denselben Weg wie der aus der Befundleiste, weil er dieselbe Auskunft
 * hat: **Datei @ Revision samt Zeilenbereich, und das genügt** (ADR-0006). Eine
 * Diff-Position stünde hier nicht, auch nicht als Bequemlichkeit — der Vorrat
 * kennt gar keinen Diff, er kennt Stellen.
 *
 * Die Seite wird nicht mehr gewählt, sondern ist die neue: Dort hat die
 * begutachtete Revision ihren Inhalt, und dorthin zeigt jeder Anker (ADR-0019).
 * Die Markierung ist damit von der Darstellung unabhängig — dieselben Zeilen
 * gelten im Diff wie in der vollständigen Datei, und der Umschalter verliert sie
 * nicht.
 */
export function markingAt(anchor: FindingAnchor): Marking {
  return {
    side: sideOfWholeFile(),
    origin: anchor.startLine,
    startLine: anchor.startLine,
    endLine: anchor.endLine,
  };
}

/**
 * Welche Zeilen ein Befund an dieser Dateiänderung nennen darf.
 *
 * Erlaubt sind die **hinzugefügten Zeilen** und die **Löschanker**: Befundet
 * wird, was die Revision geändert hat (ADR-0016), und über eine Löschung wird auf
 * der Zeile unmittelbar davor geurteilt, weil es die entfernte im Stand der
 * begutachteten Revision nicht mehr gibt (ADR-0019).
 *
 * Drei Fälle haben keinen Ort, und in allen dreien entsteht kein Befund:
 *
 * - Eine **hinzugefügte** Datei ist umgekehrt ganz geändert; die Frage nach dem
 *   Diff erübrigt sich dann.
 * - Eine **gelöschte** Datei trägt keinen Befund: Der Code ist weg, und niemand
 *   könnte ihn beheben. Lesbar bleibt sie trotzdem.
 * - Eine **übersprungene** Dateiänderung hat weder Diff noch Inhalt und damit
 *   keine Zeilenmenge. Angenommen wird keine.
 *
 * **Gekappt wird am abgelegten Inhalt:** Wie weit die Datei reicht, entscheidet
 * ihr Inhalt und nicht der Diff.
 */
export function findableLines(change: FindableFile): FindableLines {
  if (change.changeAction === "deleted") {
    return {
      ranges: [],
      withoutPlace:
        "An einer gelöschten Datei entsteht kein Befund: Der Code ist weg, " +
        "und niemand könnte ihn beheben.",
    };
  }

  if (change.content === null) {
    return {
      ranges: [],
      withoutPlace:
        "Zu dieser Dateiänderung ist kein Inhalt abgelegt; " +
        "ohne ihn gibt es keine Zeile, die ein Befund nennen könnte.",
    };
  }

  const lineCount = contentLines(change.content).length;

  if (change.changeAction === "added") {
    return {
      ranges: clamped([{ startLine: 1, endLine: lineCount }], lineCount),
      withoutPlace: null,
    };
  }

  const ranges = addedNewLines(change.hunks);

  for (const anchor of deletionAnchors(change.hunks)) {
    ranges.push({ startLine: anchor, endLine: anchor });
  }

  return { ranges: clamped(ranges, lineCount), withoutPlace: null };
}

/**
 * Der Grund, aus dem eine Markierung **nicht** zu einem Befund führt — oder
 * `null`, wenn sie es darf.
 *
 * Der Grund steht hier und nicht in der Ansicht: Sie zeigt ihn nur an. Er ist
 * derselbe, den der Server nennt, wenn er dieselbe Eingabe abweist — eine
 * Oberfläche, die anbietet, was die Eingabe verwirft, erzeugte einen Befund, der
 * in keiner Revision steht.
 *
 * **Berühren genügt** (ADR-0016): Ein Befund über drei Zeilen, von denen die
 * Revision eine angefasst hat, hat mit dieser Änderung zu tun. Auf die geänderte
 * Zeile zu kürzen verschöbe den Anker und damit die Stelle.
 */
export function reasonAgainst(
  findable: FindableLines,
  side: LineSide,
  startLine: number,
  endLine: number,
): string | null {
  if (findable.withoutPlace !== null) {
    return findable.withoutPlace;
  }

  if (side === "old") {
    return (
      "Eine entfernte Zeile trägt keinen Befund — in dieser Revision gibt es sie nicht mehr. " +
      "War das Entfernen der Fehler, liegt er auf der Zeile unmittelbar davor."
    );
  }

  for (const range of findable.ranges) {
    if (startLine <= range.endLine && endLine >= range.startLine) {
      return null;
    }
  }

  const lines =
    startLine === endLine
      ? `Zeile ${formatNumber(startLine)}`
      : `die Zeilen ${formatNumber(startLine)} bis ${formatNumber(endLine)}`;

  return (
    `Diese Revision hat ${lines} nicht geändert; ein Befund liegt auf einer hinzugefügten ` +
    "Zeile oder auf der Zeile unmittelbar vor einer Löschung."
  );
}

/** Der Anker, wie er in der Ansicht steht: Datei, Revision, Zeilenbereich. */
export function describeAnchor(anchor: FindingAnchor): string {
  const lines =
    anchor.startLine === anchor.endLine
      ? `Zeile ${formatNumber(anchor.startLine)}`
      : `Zeilen ${formatNumber(anchor.startLine)}–${formatNumber(anchor.endLine)}`;

  return `${anchor.path} @ r${formatNumber(anchor.revision)} · ${lines}`;
}

/**
 * Die Zeilen der **neuen Seite**, die dieser Diff hinzufügt oder ersetzt — als
 * zusammenhängende Bereiche, wie `Diff::addedNewLines()` im Server.
 *
 * Gezählt werden allein die **hinzugefügten** Zeilen; eine entfernte hat auf der
 * neuen Seite keine Nummer.
 */
function addedNewLines(hunks: DiffHunk[]): LineRange[] {
  const ranges: LineRange[] = [];
  let open: LineRange | null = null;

  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line.kind !== "added" || line.newLine === null) {
        continue;
      }

      if (open !== null && open.endLine + 1 === line.newLine) {
        open.endLine = line.newLine;

        continue;
      }

      open = { startLine: line.newLine, endLine: line.newLine };
      ranges.push(open);
    }
  }

  return ranges;
}

/**
 * Die **Löschanker**: je zusammenhängendem Löschblock die Zeile der neuen Seite
 * unmittelbar davor, wie `Diff::deletionAnchors()` im Server.
 *
 * Gezählt wird auf der **neuen** Seite: Die letzte Zeile, die der Abschnitt dort
 * vor dem Block zeigt — eine Kontextzeile ebenso wie eine hinzugefügte. Beginnt
 * ein Abschnitt mit einer Löschung, gibt es sie nicht, und es ist die Zeile vor
 * dem Abschnitt; ergibt das 0, ist es Zeile 1, denn Zeile 0 ist kein Ort.
 *
 * Mehrere Löschblöcke ergeben mehrere Anker, je einen.
 */
function deletionAnchors(hunks: DiffHunk[]): number[] {
  const anchors: number[] = [];

  for (const hunk of hunks) {
    let lastNewLine: number | null = null;
    let withinBlock = false;

    for (const line of hunk.lines) {
      if (line.kind === "removed") {
        if (!withinBlock) {
          anchors.push(lastNewLine ?? Math.max(1, hunk.newStart - 1));
          withinBlock = true;
        }

        continue;
      }

      withinBlock = false;

      if (line.newLine !== null) {
        lastNewLine = line.newLine;
      }
    }
  }

  return anchors;
}

/** Die Bereiche, gekappt am Ende des abgelegten Inhalts. */
function clamped(ranges: LineRange[], lineCount: number): LineRange[] {
  const kept: LineRange[] = [];

  for (const range of ranges) {
    const startLine = Math.max(1, range.startLine);
    const endLine = Math.min(range.endLine, lineCount);

    if (startLine > endLine) {
      continue;
    }

    kept.push({ startLine, endLine });
  }

  return kept;
}
